import type {
  PendingChange,
  PendingChangesResponse,
  ProductionReleaseResponse,
  ProductionReleaseSummary,
  ReleaseBuildAttempt,
} from '@diana/contracts';
import * as Dialog from '@radix-ui/react-dialog';
import { useDianaUIPortal } from '../../ui/provider/DianaUIProvider.js';
import { Check, ChevronRight, Copy, ExternalLink, LoaderCircle, RotateCcw, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type PublishDrawerProps = {
  open: boolean;
  overview: PendingChangesResponse;
  close(): void;
  navigateTo(change: PendingChange): void;
  publish(sourceRevisions: Record<string, number>): Promise<ProductionReleaseResponse>;
  load(releaseId: string): Promise<ProductionReleaseSummary>;
  loadAttempts(releaseId: string): Promise<ReleaseBuildAttempt[]>;
  retry(releaseId: string): Promise<unknown>;
  onPublished(): void;
  onPublishingChange(publishing: boolean): void;
};

export function PublishDrawer({
  open,
  overview,
  close,
  navigateTo,
  publish,
  load,
  loadAttempts,
  retry,
  onPublished,
  onPublishingChange,
}: PublishDrawerProps) {
  const portal = useDianaUIPortal();
  const opener = useRef<HTMLElement | null>(null);
  const closeButton = useRef<HTMLButtonElement | null>(null);
  const [releaseId, setReleaseId] = useState<string | null>(null);
  const [release, setRelease] = useState<ProductionReleaseSummary | null>(null);
  const [attempts, setAttempts] = useState<ReleaseBuildAttempt[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A failure to create the release and a failure to read it back are different
  // facts. Only the first means the website is untouched, and saying so when a
  // build is already running sends the owner to publish a second time.
  const [statusUnavailable, setStatusUnavailable] = useState(false);
  // Release creation can outlive the request that asked for it, so a failed
  // request does not prove the website is untouched.
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);
  const [diagnosticsCopied, setDiagnosticsCopied] = useState(false);
  const [clock, setClock] = useState(() => Date.now());
  const publishedRelease = useRef<string | null>(null);
  const publishStartedAt = useRef<number | null>(null);

  const settled = Boolean(release && ['Ready', 'Failed', 'TimedOut'].includes(release.status));
  const publishing =
    busy ||
    Boolean(release && ['Queued', 'Building'].includes(release.status)) ||
    // The release exists but its status has not been read yet, so a build is
    // running whether or not this browser can see it.
    Boolean(releaseId && !release && !error);

  useEffect(() => onPublishingChange(publishing), [onPublishingChange, publishing]);

  const refresh = useCallback(
    async (id: string) => {
      const [nextRelease, nextAttempts] = await Promise.all([load(id), loadAttempts(id)]);
      setRelease(nextRelease);
      setAttempts(nextAttempts);
      setStatusUnavailable(false);
      if (nextRelease.status === 'Ready' && publishedRelease.current !== id) {
        publishedRelease.current = id;
        onPublished();
      }
    },
    [load, loadAttempts, onPublished],
  );

  // Polling used to require a release that had already been read once, so a
  // single failed read left the drawer blind for good while the build carried on.
  // Keep polling until the release actually settles.
  useEffect(() => {
    if (!releaseId || settled) return;
    const timer = window.setInterval(() => {
      void refresh(releaseId).catch(() => setStatusUnavailable(true));
    }, 2_500);
    return () => window.clearInterval(timer);
  }, [refresh, releaseId, settled]);

  useEffect(() => {
    if (!publishing) return;
    setClock(Date.now());
    const timer = window.setInterval(() => setClock(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [publishing]);

  const startPublish = async () => {
    if (publishing) return;
    setBusy(true);
    setError(null);
    setRelease(null);
    setAttempts([]);
    setDiagnosticsCopied(false);
    publishedRelease.current = null;
    publishStartedAt.current = Date.now();
    setStatusUnavailable(false);
    setOutcomeUnknown(false);
    setClock(Date.now());
    try {
      const created = await publish(overview.sourceRevisions);
      setReleaseId(created.releaseId);
      // Past this point the release exists and the build has been dispatched.
      // A read that fails now costs visibility, not the publish.
      try {
        await refresh(created.releaseId);
      } catch {
        setStatusUnavailable(true);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Website could not be published');
      setOutcomeUnknown(!refusedBeforeCreating(caught));
    } finally {
      setBusy(false);
    }
  };

  const retryPublish = async () => {
    if (!releaseId || publishing) return;
    setBusy(true);
    setError(null);
    setDiagnosticsCopied(false);
    publishedRelease.current = null;
    publishStartedAt.current = Date.now();
    setStatusUnavailable(false);
    setOutcomeUnknown(false);
    setClock(Date.now());
    try {
      await retry(releaseId);
      try {
        await refresh(releaseId);
      } catch {
        setStatusUnavailable(true);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Publish retry could not start');
      setOutcomeUnknown(!refusedBeforeCreating(caught));
    } finally {
      setBusy(false);
    }
  };

  const latestAttempt = attempts[attempts.length - 1] ?? null;
  const progress = useMemo(
    () =>
      publishProgress(
        busy,
        release,
        error,
        statusUnavailable,
        outcomeUnknown,
        stageDurations(release, latestAttempt, publishStartedAt.current, clock),
      ),
    [busy, clock, error, latestAttempt, outcomeUnknown, release, statusUnavailable],
  );
  const diagnostic = useMemo(
    () => parsePublishDiagnostic(latestAttempt?.errorSummary ?? release?.diagnosticSummary ?? null),
    [latestAttempt?.errorSummary, release?.diagnosticSummary],
  );
  const timing = useMemo(
    () => publishTiming(publishing, clock, release, latestAttempt, publishStartedAt.current, error),
    [clock, error, latestAttempt, publishing, release],
  );
  const showProgress = Boolean(release || busy || error || statusUnavailable || outcomeUnknown);

  const changes = (
    <div className="publish-drawer__changes">
      {overview.changes.map((change) => (
        <button
          className="publish-drawer-change"
          key={change.key}
          type="button"
          onClick={() => navigateTo(change)}
          disabled={!adminPath(change)}
        >
          <span className="publish-drawer-change__main">
            <strong>{change.label}</strong>
            <small>
              {change.details.join(' · ')}
              {change.changedAt ? ` · ${formatRelativeDate(change.changedAt)}` : ''}
            </small>
            <span>
              {change.siteAccess === 'AdminOnly' ? 'Admin only' : 'Affects website'}
              {change.route ? ` · ${change.route}` : ''}
            </span>
          </span>
          {adminPath(change) ? <ChevronRight size={16} aria-hidden="true" /> : null}
        </button>
      ))}
    </div>
  );

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
    >
      <Dialog.Portal container={portal}>
        <Dialog.Overlay className="publish-drawer-backdrop" data-testid="publish-backdrop" />
        <Dialog.Content
          className="publish-drawer"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            opener.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
            closeButton.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const target =
              opener.current?.isConnected &&
              opener.current !== document.body &&
              opener.current !== document.documentElement
                ? opener.current
                : document.querySelector<HTMLElement>('.admin-main');
            target?.focus();
          }}
        >
          <div className="publish-drawer__grabber" aria-hidden="true">
            <span />
          </div>
          <header className="publish-drawer__header">
            <div>
              <p className="eyebrow">Website release</p>
              <Dialog.Title asChild>
                <h2>{overview.count === 0 ? 'Website release' : 'Pending changes'}</h2>
              </Dialog.Title>
              <Dialog.Description asChild>
                <p>
                  {overview.count === 0
                    ? 'Content is up to date. Republish to roll out the latest website update.'
                    : `${overview.count} saved change${overview.count === 1 ? '' : 's'} waiting to go live.`}
                </p>
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                ref={closeButton}
                className="publish-drawer__close"
                type="button"
                aria-label="Close publish panel"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </Dialog.Close>
          </header>

          <div className="publish-drawer__body">
            {showProgress ? (
              <section className="publish-progress" aria-live="polite">
                <div className="publish-progress__heading">
                  <div>
                    <strong>{progress.title}</strong>
                    {timing ? <time aria-hidden="true">{timing}</time> : null}
                  </div>
                  <small>{progress.description}</small>
                </div>
                <ol>
                  {progress.steps.map((step) => (
                    <li key={step.label} data-state={step.state}>
                      <span aria-hidden="true">
                        {step.state === 'done' ? (
                          <Check size={13} />
                        ) : step.state === 'active' ? (
                          <LoaderCircle className="publish-stage-spinner" size={13} />
                        ) : null}
                      </span>
                      {step.label}
                      {step.duration !== null && (step.duration > 0 || step.state === 'active') ? (
                        <time aria-hidden="true">{formatWordDuration(step.duration)}</time>
                      ) : null}
                    </li>
                  ))}
                </ol>
                {diagnostic ? (
                  <div className="publish-diagnostic" role="alert">
                    <p>{diagnostic.message}</p>
                    <details>
                      <summary>Technical details</summary>
                      <dl>
                        <div>
                          <dt>Code</dt>
                          <dd>{diagnostic.code}</dd>
                        </div>
                        <div>
                          <dt>Stage</dt>
                          <dd>Building and deploying website</dd>
                        </div>
                        {diagnostic.field ? (
                          <div>
                            <dt>Field</dt>
                            <dd>{diagnostic.field}</dd>
                          </div>
                        ) : null}
                        {release ? (
                          <div>
                            <dt>Release</dt>
                            <dd>{release.id}</dd>
                          </div>
                        ) : null}
                        {latestAttempt ? (
                          <div>
                            <dt>Attempt</dt>
                            <dd>{latestAttempt.attemptNumber}</dd>
                          </div>
                        ) : null}
                      </dl>
                      <div className="publish-diagnostic__actions">
                        <button
                          className="button button--quiet"
                          type="button"
                          onClick={() => {
                            const details = copyablePublishDiagnostic(
                              diagnostic,
                              release,
                              latestAttempt,
                            );
                            void navigator.clipboard
                              .writeText(details)
                              .then(() => setDiagnosticsCopied(true))
                              .catch(() => setDiagnosticsCopied(false));
                          }}
                        >
                          <Copy size={14} aria-hidden="true" />
                          {diagnosticsCopied ? 'Copied' : 'Copy diagnostics'}
                        </button>
                        {githubRunUrl(latestAttempt?.githubRunId) ? (
                          <a
                            className="button button--quiet"
                            href={githubRunUrl(latestAttempt?.githubRunId)!}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Open GitHub run
                            <ExternalLink size={13} aria-hidden="true" />
                          </a>
                        ) : null}
                      </div>
                    </details>
                  </div>
                ) : null}
                {error ? (
                  <p className="form-error" role="alert">
                    {error}
                  </p>
                ) : null}
                {release?.status === 'Failed' || release?.status === 'TimedOut' ? (
                  <button
                    className="button button--quiet"
                    type="button"
                    disabled={busy}
                    onClick={() => void retryPublish()}
                  >
                    <RotateCcw size={15} aria-hidden="true" />
                    Try again
                  </button>
                ) : null}
                {release?.status === 'Ready' ? (
                  <a
                    className="button button--quiet"
                    href="https://perova.photo/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    View website
                    <ExternalLink size={14} aria-hidden="true" />
                  </a>
                ) : null}
                {attempts.length > 1 ? <small>{attempts.length} publish attempts</small> : null}
              </section>
            ) : null}

            {overview.changes.length ? (
              showProgress ? (
                <details className="publish-drawer__changes-group">
                  <summary>
                    <span>Included changes</span>
                    <span>
                      {overview.count}
                      <ChevronRight size={15} aria-hidden="true" />
                    </span>
                  </summary>
                  {changes}
                </details>
              ) : (
                changes
              )
            ) : null}
          </div>

          <footer className="publish-drawer__footer">
            {overview.lastPublishedAt ? (
              <small>Last published {formatDate(overview.lastPublishedAt)}</small>
            ) : (
              <small>No website release has been published yet.</small>
            )}
            <button
              className="button button--primary publish-drawer__publish"
              type="button"
              disabled={publishing}
              onClick={() => void startPublish()}
              aria-busy={publishing || undefined}
            >
              {publishing ? (
                <LoaderCircle className="publish-spinner" size={15} aria-hidden="true" />
              ) : null}
              {publishing
                ? 'Publishing…'
                : overview.count === 0
                  ? 'Republish website'
                  : `Publish ${overview.count} change${overview.count === 1 ? '' : 's'}`}
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function adminPath(change: PendingChange) {
  if (change.kind === 'Page') return `/pages/${change.key.slice('page:'.length)}`;
  if (change.kind === 'Gallery') return `/shoots/${change.key.slice('gallery:'.length)}`;
  if (change.kind === 'Journal') return `/journal/${change.key.slice('journal:'.length)}`;
  if (change.kind === 'Pricing') return '/packages';
  if (change.kind === 'Settings') return '/settings';
  if (change.kind === 'Media') return '/settings/media';
  return null;
}

function publishProgress(
  busy: boolean,
  release: ProductionReleaseSummary | null,
  error: string | null,
  statusUnavailable: boolean,
  outcomeUnknown: boolean,
  durations: StageDurations,
) {
  if (release?.status === 'Ready') {
    return {
      title: 'Published successfully',
      description: `Completed ${formatDate(release.updatedAt)}`,
      steps: steps('done', 'done', 'done', durations),
    };
  }
  if (release?.status === 'Failed' || release?.status === 'TimedOut') {
    return {
      title: 'Publishing stopped',
      description: 'The current website is unchanged.',
      steps: steps('done', 'error', 'pending', durations),
    };
  }
  if (outcomeUnknown && !release) {
    return {
      title: 'Publish status unknown',
      description:
        'The request did not come back. The release may already have been created and the website may be updating. Open the website to check before publishing again.',
      steps: steps('active', 'pending', 'pending', durations),
    };
  }
  // Only a refusal to create the release means nothing happened. Everything else
  // is a lost connection to work that may well be running.
  if (error && !release) {
    return {
      title: 'Publishing stopped',
      description: 'The current website is unchanged.',
      steps: steps('error', 'pending', 'pending', durations),
    };
  }
  if (statusUnavailable) {
    return {
      title: 'Publishing website',
      description:
        'The release was created and is being built. Its progress cannot be read right now — this page keeps trying. Do not publish again.',
      steps: steps('done', 'active', 'pending', durations),
    };
  }
  if (release?.status === 'Building') {
    return {
      title: 'Publishing website',
      description: 'The new version is being built and deployed. The current website stays live.',
      steps: steps('done', 'active', 'pending', durations),
    };
  }
  return {
    title: 'Preparing content',
    description: busy
      ? 'Creating an exact snapshot of the saved changes.'
      : 'Waiting for the build to start.',
    steps: steps('active', 'pending', 'pending', durations),
  };
}

// A conflict, a validation failure or a rejected identity is the API deciding
// not to create anything, so nothing was published. A timeout, a 5xx or a
// dropped connection says only that the answer never came back: release
// creation runs for over a minute and can outlive the request carrying it, and
// the owner has twice been told the website was unchanged while it was being
// rebuilt.
function refusedBeforeCreating(caught: unknown) {
  const kind = (caught as { kind?: unknown } | null)?.kind;
  return (
    kind === 'conflict' || kind === 'validation' || kind === 'unauthorized' || kind === 'forbidden'
  );
}

// The three spans abut deliberately: preparing ends where building begins and
// building ends where publishing begins, so no waiting can hide between them and
// the parts add up to the total in the heading. A stage that has not begun
// reports nothing rather than zero.
function stageDurations(
  release: ProductionReleaseSummary | null,
  attempt: ReleaseBuildAttempt | null,
  localStartedAt: number | null,
  now: number,
): StageDurations {
  const at = (value: string | null | undefined) => (value ? new Date(value).getTime() : null);
  const settled =
    release && ['Ready', 'Failed', 'TimedOut'].includes(release.status)
      ? at(release.updatedAt)
      : null;
  // The boundary has to be where the drawer draws it. Preparing stays the active
  // step until the build genuinely starts, so queueing belongs to preparing and
  // not to a row still showing a pending circle. Ending preparing at release
  // creation read it as 8s and charged the following minute and a half to a
  // build that had not begun.
  const dispatched = at(attempt?.createdAt) ?? at(release?.createdAt);
  const buildStarted = at(attempt?.startedAt);
  const built = at(attempt?.finishedAt);
  const opened = [localStartedAt, dispatched].filter(
    (value): value is number => typeof value === 'number',
  );
  const started = opened.length > 0 ? Math.min(...opened) : null;

  const span = (from: number | null, to: number | null) =>
    from === null ? null : Math.max(0, (to ?? settled ?? now) - from);

  return {
    preparing: span(started, buildStarted),
    building: span(buildStarted, built),
    published: span(built, settled),
  };
}

type StageDurations = {
  preparing: number | null;
  building: number | null;
  published: number | null;
};

function steps(
  preparing: ProgressState,
  building: ProgressState,
  published: ProgressState,
  durations: StageDurations,
) {
  return [
    { label: 'Preparing content', state: preparing, duration: durations.preparing },
    {
      label: 'Building and deploying website',
      state: building,
      duration: durations.building,
    },
    { label: 'Published', state: published, duration: durations.published },
  ];
}

type ProgressState = 'pending' | 'active' | 'done' | 'error';

type PublishDiagnostic = {
  code: string;
  message: string;
  field: string | null;
};

export function parsePublishDiagnostic(value: string | null): PublishDiagnostic | null {
  if (!value) return null;
  const [code, message, field, ...extra] = value.split('::');
  if (extra.length === 0 && /^[A-Z][A-Z0-9_]+$/.test(code ?? '') && Boolean(message)) {
    return { code, message, field: field || null };
  }
  return { code: 'BUILD_FAILED', message: value, field: null };
}

function copyablePublishDiagnostic(
  diagnostic: PublishDiagnostic,
  release: ProductionReleaseSummary | null,
  attempt: ReleaseBuildAttempt | null,
) {
  return [
    `Code: ${diagnostic.code}`,
    `Message: ${diagnostic.message}`,
    'Stage: Building and deploying website',
    ...(diagnostic.field ? [`Field: ${diagnostic.field}`] : []),
    ...(release ? [`Release: ${release.id}`, `Recorded: ${release.updatedAt}`] : []),
    ...(attempt ? [`Attempt: ${attempt.attemptNumber}`] : []),
    ...(attempt?.githubRunId ? [`GitHub run: ${attempt.githubRunId}`] : []),
  ].join('\n');
}

function githubRunUrl(runId: string | null | undefined) {
  return runId && /^\d+$/.test(runId)
    ? `https://github.com/igor-perov/diana-photo/actions/runs/${runId}`
    : null;
}

function publishTiming(
  publishing: boolean,
  now: number,
  release: ProductionReleaseSummary | null,
  attempt: ReleaseBuildAttempt | null,
  localStartedAt: number | null,
  error: string | null,
) {
  // Report the whole wait, not the build alone. Preferring attempt.startedAt used
  // to move the start forward the moment a runner picked the job up, so the
  // counter jumped backwards mid-publish and the final figure omitted everything
  // before the build. Take the earliest moment known instead, which for a live
  // publish is the click and for a reopened drawer is the attempt. An attempt's
  // own timestamps replace the release's, so a retry times the retry rather than
  // the original publish.
  const recordedStart = attempt ? (attempt.createdAt ?? attempt.startedAt) : release?.createdAt;
  const candidates = [localStartedAt, recordedStart ? new Date(recordedStart).getTime() : null];
  const known = candidates.filter((value): value is number => typeof value === 'number');
  const startedAt = known.length > 0 ? Math.min(...known) : null;
  if (!startedAt) return null;

  // The publish ends when the release settles, not when the build binary stops:
  // the signed callback still has to land before the website is actually live.
  // Ending at attempt.finishedAt would also leave the per-stage figures unable
  // to sum to this total.
  const settled =
    release && ['Ready', 'Failed', 'TimedOut'].includes(release.status)
      ? new Date(release.updatedAt).getTime()
      : null;
  const finishedAt =
    settled ?? (attempt?.finishedAt ? new Date(attempt.finishedAt).getTime() : null);
  const elapsed = Math.max(0, (finishedAt ?? now) - startedAt);

  if (publishing) return `${formatClockDuration(elapsed)} elapsed`;
  if (release?.status === 'Ready') return `Published in ${formatWordDuration(elapsed)}`;
  if (error || release?.status === 'Failed' || release?.status === 'TimedOut') {
    return `Stopped after ${formatWordDuration(elapsed)}`;
  }
  return null;
}

function formatClockDuration(value: number) {
  const totalSeconds = Math.floor(value / 1_000);
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    : `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function formatWordDuration(value: number) {
  const totalSeconds = Math.max(1, Math.round(value / 1_000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (!minutes) return `${seconds}s`;
  return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function formatRelativeDate(value: string) {
  const elapsed = Date.now() - new Date(value).getTime();
  if (elapsed < 60_000) return 'Saved just now';
  if (elapsed < 3_600_000) return `Saved ${Math.max(1, Math.floor(elapsed / 60_000))}m ago`;
  return `Saved ${formatDate(value)}`;
}
